import React, { useEffect, useMemo, useState } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
import { useParams } from "react-router-dom";
import { CustomButton } from "../../components/CustomButton";
import {
  API_GET_USERS,
  API_GET_USER_LIMIT,
  API_UPDATE_USER_LIMIT,
} from "../../api/company.service";

const limitLabels = [
  { key: "adminCount", label: "Admin seats" },
  { key: "supportCount", label: "Support seats" },
  { key: "campaignCount", label: "Campaign seats" },
  { key: "orderCount", label: "Order seats" },
];

export const Users = () => {
  const { companyId } = useParams();
  const [users, setUsers] = useState([]);
  const [limits, setLimits] = useState({
    supportCount: 0,
    adminCount: 0,
    campaignCount: 0,
    orderCount: 0,
  });
  const [showDialog, setShowDialog] = useState(false);
  const [dropdownOptions, setDropdownOptions] = useState([]);

  const generateDropdownOptions = (currentValues) => {
    const max = Math.max(200, ...Object.values(currentValues));
    return Array.from({ length: max + 1 }, (_, index) => ({
      label: `${index}`,
      value: index,
    }));
  };

  useEffect(() => {
    if (!companyId) return;

    const fetchData = async () => {
      try {
        const usersRes = await API_GET_USERS(companyId);
        const limitsRes = await API_GET_USER_LIMIT(companyId);

        const latestLimits = {
          supportCount: limitsRes?.supportCount ?? 0,
          adminCount: limitsRes?.adminCount ?? 0,
          campaignCount: limitsRes?.campaignCount ?? 0,
          orderCount: limitsRes?.orderCount ?? 0,
        };

        setUsers(usersRes?.user?.rows || []);
        setLimits(latestLimits);
        setDropdownOptions(generateDropdownOptions(latestLimits));
      } catch (err) {
        console.error("Failed to fetch users or limits:", err);
      }
    };

    fetchData();
  }, [companyId]);

  const roleBreakdown = useMemo(() => {
    return users.reduce(
      (acc, user) => {
        const role = String(user.role || "").toLowerCase();
        if (role.includes("admin")) acc.admin += 1;
        else if (role.includes("support")) acc.support += 1;
        else acc.other += 1;
        return acc;
      },
      { admin: 0, support: 0, other: 0 }
    );
  }, [users]);

  const handleLimitChange = (event, key) => {
    setLimits((prev) => ({ ...prev, [key]: event.value }));
  };

  const handleSave = async () => {
    try {
      await API_UPDATE_USER_LIMIT(companyId, limits);
      const updated = await API_GET_USER_LIMIT(companyId);
      const latestLimits = {
        supportCount: updated?.supportCount ?? 0,
        adminCount: updated?.adminCount ?? 0,
        campaignCount: updated?.campaignCount ?? 0,
        orderCount: updated?.orderCount ?? 0,
      };
      setLimits(latestLimits);
      setDropdownOptions(generateDropdownOptions(latestLimits));
      setShowDialog(false);
    } catch (err) {
      console.error("Failed to update limits:", err);
    }
  };

  const convertDate = (user) => {
    const createdDate = new Date(user.createdAt);
    return <span>{createdDate.toLocaleString()}</span>;
  };

  return (
    <div className="company-user-layout">
      <div className="company-user-limits">
        <div className="company-insight-card">
          <span>Total Users</span>
          <strong>{users.length}</strong>
        </div>
        <div className="company-insight-card">
          <span>Admin Users</span>
          <strong>{roleBreakdown.admin}</strong>
        </div>
        <div className="company-insight-card">
          <span>Support Users</span>
          <strong>{roleBreakdown.support}</strong>
        </div>
        <div className="company-insight-card">
          <span>Other Users</span>
          <strong>{roleBreakdown.other}</strong>
        </div>
      </div>

      <div className="surface-panel">
        <div className="surface-panel-header">
          <div>
            <h2 className="surface-panel-title">User registry</h2>
          </div>
          <CustomButton
            label="Configure Limits"
            icon="pi pi-sliders-h"
            varient="outline"
            onClick={() => {
              setDropdownOptions(generateDropdownOptions(limits));
              setShowDialog(true);
            }}
          />
        </div>

        <div className="company-user-limits">
          {limitLabels.map((item) => (
            <div className="company-insight-card" key={item.key}>
              <span>{item.label}</span>
              <strong>{limits[item.key]}</strong>
            </div>
          ))}
        </div>

        <div className="company-table-wrap" style={{ marginTop: "1rem" }}>
          <DataTable
            value={users}
            paginator
            rows={6}
            rowsPerPageOptions={[6, 12, 24]}
            responsiveLayout="scroll"
          >
            <Column field="id" header="ID" />
            <Column field="userName" header="User Name" />
            <Column field="name" header="Name" />
            <Column field="role" header="Role" />
            <Column field="isActive" header="Active" />
            <Column header="Created At" body={convertDate} />
          </DataTable>
        </div>
      </div>

      <Dialog
        header="Update User Limits"
        visible={showDialog}
        style={{ width: "min(620px, 92vw)" }}
        onHide={() => setShowDialog(false)}
      >
        <div className="company-dialog-grid">
          {limitLabels.map(({ key, label }) => (
            <div key={key} className="company-dialog-field">
              <label htmlFor={key}>{label}</label>
              <Dropdown
                inputId={key}
                value={limits[key]}
                options={dropdownOptions}
                onChange={(event) => handleLimitChange(event, key)}
                placeholder="Select limit"
              />
            </div>
          ))}
        </div>
        <div className="company-dialog-actions">
          <CustomButton
            label="Cancel"
            varient="outline"
            onClick={() => setShowDialog(false)}
          />
          <CustomButton label="Save Limits" onClick={handleSave} />
        </div>
      </Dialog>
    </div>
  );
};
